/**
 * Trips and the plans generated for them: EPIC 4.
 *
 * This is the part of the product where a fabricated number does the most damage, so the schema says
 * so explicitly rather than leaving it to a convention.
 *
 * **Every plan metric is nullable, and `null` means "the routing provider did not answer".** Spec §18
 * forbids showing a distance the provider did not return, and AC-6.1 makes the degraded state a
 * requirement rather than a nicety: when routing is down the roadmap shows stop markers, shows
 * "chưa xác định" where a distance would go, and says why. A non-null default would make that
 * impossible to express — a `0` reads as "these two stops are the same place", which is a different
 * and false statement.
 *
 * **A constraint that could not be satisfied is named, never dropped.** `unsatisfied` carries the
 * constraints that give, because the client has to show the user which one to relax (AC-4.5). A plan
 * that silently ignored a driving cap would be a plan the user cannot trust and cannot fix.
 *
 * **`planner` is the only author of these numbers.** The schema comment on `plan_variants` in the
 * database says "never LLM output", and the contract's `explanation.generated_by` distinguishes the
 * two so a client can render a fact list without prose when the model is unavailable.
 */

import { z } from "zod";

import { Id, IsoDate, IsoDateTime } from "./common.ts";
import { ProviderStatus } from "./places.ts";

// ------------------------------------------------------------------------------------- the trip

export const TransportMode = z.enum(["car", "motorbike", "bicycle", "walk", "public_transport"]).meta({
  id: "TransportMode",
  description: "How the party moves. `car` and `motorbike` are what Vietnam actually uses; the rest exist so the enum is not a lie.",
});

/**
 * The union, exported so a client can hold one in state without reaching for `Trip["transport"]`.
 *
 * Every other schema in this file exports its inferred type; this one did not, and the cost was a screen
 * declaring its own `"car" | "motorbike" | "walk"` — three of five members, silently narrower than the
 * contract, which is how `bicycle` and `public_transport` became unreachable from the UI.
 */
export type TransportMode = z.infer<typeof TransportMode>;

export const TripStatus = z.enum(["draft", "planned", "completed", "archived"]).meta({
  id: "TripStatus",
  description: "Where the trip is. `draft` until a plan is selected.",
});

export const TripVisibility = z.enum(["private", "shared", "public"]).meta({
  id: "TripVisibility",
  description: "MVP creates `private`. Declared so the default is explicit rather than absent.",
});

export const Trip = z.object({
  id: Id,
  title: z.string().min(1),
  destination: z.string().min(1),
  /** Calendar dates, not instants: a trip is a range of days, and an instant would drag a timezone in. */
  start_date: IsoDate,
  end_date: IsoDate,
  adults: z.int().gte(1),
  children: z.int().gte(0),
  transport: TransportMode,
  /**
   * How the party travels, in the user's own words, when the enum cannot hold it.
   *
   * `transport` is what the planner computes with — it needs a speed, and a speed cannot be read out of
   * free text. This is what the user actually said: "xe khách lên Đà Lạt", "thuê xe 7 chỗ có tài", "tàu
   * hỏa từ Sài Gòn" are three different trips that all collapse into `public_transport`.
   *
   * `null` means they picked one of the named modes, which is a different fact from having described
   * their own — and it is why this is not defaulted to an empty string.
   */
  transport_note: z.string().nullable(),
  /**
   * Whether the trip ends with a night away.
   *
   * "2 ngày 1 đêm" and "2 ngày 2 đêm" are different trips, and a traveller says which — so the count is
   * stored rather than inferred twice. False means `nights = days - 1`: you sleep between the days and go
   * home on the last one, which is what a date range implies.
   */
  stay_last_night: z.boolean(),
  /**
   * Where day 1 starts: a saved place — the bus station, the airport — or null.
   *
   * A place and not free text, because a route has to be measured from it, and free text has no
   * coordinate. Null means day 1 begins at its first visit, which is what every plan did before this
   * field existed.
   */
  entry_place_id: Id.nullable(),
  /**
   * How many places the trip currently selects: the `trip_constraint_places` rows of kind `must_visit`, which
   * are the same selection `TripResponse.place_ids` returns.
   *
   * A count and not the ids, because a card says "12 địa điểm" and a list of twelve ids for one number is
   * work the server can do once and the client should not have to.
   */
  place_count: z.int().gte(0),
  /**
   * How many nights the trip has — the same number as `TripResponse.nights.length`, without the array.
   *
   * Why the server counts them rather than the client is the argument `TripResponse.nights` already makes
   * for enumerating them; this is that rule one level up, for the list that shows a count and not the
   * nights themselves.
   */
  nights_count: z.int().gte(0),
  status: TripStatus,
  visibility: TripVisibility,
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
}).meta({ id: "Trip", description: "A trip: a destination, a date range, and who is going." });

export type Trip = z.infer<typeof Trip>;

export const TripPage = z.object({
  items: z.array(Trip),
  next_cursor: z.string().nullable(),
}).meta({ id: "TripPage", description: "The caller's trips, newest first." });

/**
 * The constraints a plan must respect.
 *
 * **Only what binds survives here.** The screen measured its own controls against the engine on 2026-10-02:
 * `sleep_time`, `daily_rest_minutes`, `max_walking_km`, `budget_min`, `budget_max`, `currency` and
 * `avoid_tags` changed no plan, and a control the product offers and the engine ignores is worse than no
 * control — the user sets it and the plan silently disagrees. `sleep_time` and `daily_rest_minutes` were
 * offered on the constraint screen; the rest were never read anywhere. The schedule takes its rest from the
 * strategy (`STRATEGIES[].restMin`), which is where it always came from, so the removed `restMin` input
 * changes nothing.
 *
 * Every surviving field has a defensible default, which is the point: spec §5.2 of the UX plan says the
 * constraints screen is "preselected and collapsed", because asking a first-time user to configure numbers
 * before seeing anything is how a planner goes unused.
 *
 * `max_driving_minutes` is explicitly nullable and **not defaulted to a number**. It is also **advisory**:
 * the plan shapes itself around `wake_time`, `check_in_time` and the pace, and a day over the stated cap is
 * named in `unsatisfied` rather than trimmed, reordered or dropped — measured on the seeded data, a cap of
 * 30 minutes produced the same stops, order, times and distance as no cap at all, and one warning per
 * offending day. The label says so.
 *
 * **The screen that offered these three is gone; the fields stay.** The constraint step was removed because
 * it changed nothing measurable: `wake_time` and `check_in_time` only shift clock times after the stops and
 * their order are already fixed, `max_driving_minutes` only appends a warning and never trims, reorders or
 * drops a stop, and under a user arrangement all three are inert because the engine bypasses the
 * arrangement entirely. The fields are kept anyway because removing one from a published request is a
 * breaking change that needs a version bump, and because the plan handler reads all three from the trip's
 * own row when it generates (`apps/api/src/handlers/trips.ts`). A new trip therefore takes the defaults
 * above; no client offers them.
 */
export const TripConstraints = z.object({
  /** Local time the day starts, `HH:MM`. */
  wake_time: z.string().regex(/^\d{2}:\d{2}$/).default("08:00"),
  /**
   * The earliest a lodging stop can be placed, `HH:MM`.
   *
   * A floor, not a schedule: it says you cannot check in before this, so a day whose visits finish early
   * waits rather than arriving to a locked door. 14:00 is the Vietnamese hotel default and it is editable,
   * because a homestay that hands over keys at 15:00 is not wrong.
   */
  check_in_time: z.string().regex(/^\d{2}:\d{2}$/).default("14:00"),
  /**
   * A daily driving cap in minutes, **advisory**.
   *
   * When a day's measured driving exceeds it, that day is named in the plan's `unsatisfied` list; the plan
   * keeps the stops the user arranged. Null is the default and means "not stated", which is not a cap of
   * zero — see the object comment.
   */
  max_driving_minutes: z.int().positive().nullable().default(null),
  /**
   * Pace, which the three strategies scale from.
   *
   * Not the same thing as the strategy: pace is what the user says about themselves, strategy is which
   * of the three plans they are looking at. Conflating them would mean choosing "Khám phá" silently
   * rewrote the user's stated preference.
   */
  pace: z.enum(["relaxed", "balanced", "packed"]).default("balanced"),
}).meta({ id: "TripConstraints", description: "What a plan must respect. Every field has a defensible default." });

export type TripConstraints = z.infer<typeof TripConstraints>;

/**
 * One night of a trip, and where it is spent.
 *
 * Identified by its **date** rather than by an index, because the date is what the user says — "đêm 28/9"
 * — and because an index would have to be renumbered whenever the trip's dates changed. The client's job
 * is then to notice that a night no longer exists, rather than to discover that every night after the
 * change means something different.
 *
 * `place_id` is null for a night the user has not assigned. That is a real answer and not a gap to be
 * filled: a trip whose middle night is unplanned plans fine, and the day's route simply is not anchored
 * at either end. It is also what a night becomes when its lodging is deleted — `ON DELETE SET NULL`
 * rather than a cascade, so one deleted hotel cannot silently renumber a trip.
 */
export const TripNight = z.object({
  night_date: IsoDate,
  place_id: Id.nullable(),
}).meta({ id: "TripNight", description: "One night and the place it is spent in, or null when unassigned." });

/**
 * One place, on one day of the trip, as the user arranged it.
 *
 * **The same place may appear on several days** — a favourite café on two mornings is a real trip, and the
 * contract does not forbid it. The client marks the later appearance with the day it was first on rather
 * than preventing it.
 *
 * A selected place with **no** entry is left out of the plan. That is the whole meaning of absence here, and
 * it is stated because the alternative reading — "no entry means the planner decides" — is the one a client
 * would otherwise assume, and it would make a half-finished arrangement silently plan the rest itself.
 */
/**
 * When in a day the user means to be somewhere.
 *
 * **Deliberately not `BestTimeSlot`**, which is the four values a *place* is asked to describe itself with
 * (`sáng`, `trưa`, `chiều-tối`, `cả ngày`). That is a property of the place — "this café is good in the
 * morning" — and this is a decision about a day — "we are going at six". The same distinction the contract
 * already draws between `pace` and `strategy`: one is what the user says about themselves, the other is
 * what the plan is doing. `chiều-tối` is a defensible answer about a place and a useless one about a
 * schedule, which is why the decision splits it.
 *
 * A client offers only the values a place suits (`best_time`), and that filtering is an affordance rather
 * than a server rule: a stored arrangement must not become invalid because the place's own answer changed
 * afterwards.
 */
export const TripSlot = z.enum(["sáng", "trưa", "chiều", "tối", "cả ngày"]).meta({
  id: "TripSlot",
  description: "When in the day, as a plan decision. Distinct from a place's own `best_time`.",
});

export type TripSlot = z.infer<typeof TripSlot>;

export const TripPlaceDay = z.object({
  place_id: Id,
  day_index: z.int().gte(1),
  /** Absent means the user said nothing about the time, which is not the same as `cả ngày`. */
  time_slot: TripSlot.nullable().optional(),
}).meta({ id: "TripPlaceDay", description: "A place the user put on a day of the trip." });

export const CreateTripRequest = z.object({
  title: z.string().min(1).max(120),
  destination: z.string().min(1).max(120),
  start_date: IsoDate,
  end_date: IsoDate,
  adults: z.int().gte(1).max(20).default(1),
  children: z.int().gte(0).max(20).default(0),
  transport: TransportMode.default("car"),
  /** The user's own words for how they travel. Absent or null means a named mode was chosen. */
  transport_note: z.string().max(200).nullable().optional(),
  /** Omitted means the defaults apply, which is what the collapsed constraints screen sends. */
  constraints: TripConstraints.partial().optional(),
  /** Places to plan with. Omitted or empty is rejected at plan time, not here: a trip can exist before it has places. */
  place_ids: z.array(Id).optional(),
  /** Omitted means one night per day except the last. */
  stay_last_night: z.boolean().optional(),
  entry_place_id: Id.nullable().optional(),
  /** `place_id: null` is an unassigned night, which is stored rather than skipped. */
  nights: z.array(TripNight).optional(),
  /** The user's arrangement. Omitted or empty means the planner computes the days itself. */
  place_days: z.array(TripPlaceDay).optional(),
  /**
   * The days the party returns to its lodging to rest, 1-based.
   *
   * A list of days rather than a flag on each, because it is the unusual day that does not come back — a day
   * driving to the far side of the province rests where it stops. Storing only the exceptions means the common
   * case costs nothing and an older trip, which has no rows, behaves exactly as it did.
   */
  rest_days: z.array(z.int().gte(1)).optional(),
}).meta({ id: "CreateTripRequest", description: "POST /trips. A trip can exist before it has places." });

/**
 * `PATCH /trips/:id` — every field optional, an omitted field left alone.
 *
 * `place_ids` replaces the selection rather than adding to it, for the same reason
 * `travel_style` replaces its map: an add-only operation makes removing a place impossible without a
 * second endpoint.
 */
export const UpdateTripRequest = z.object({
  title: z.string().min(1).max(120).optional(),
  destination: z.string().min(1).max(120).optional(),
  start_date: IsoDate.optional(),
  end_date: IsoDate.optional(),
  adults: z.int().gte(1).max(20).optional(),
  children: z.int().gte(0).max(20).optional(),
  transport: TransportMode.optional(),
  transport_note: z.string().max(200).nullable().optional(),
  constraints: TripConstraints.partial().optional(),
  place_ids: z.array(Id).optional(),
  stay_last_night: z.boolean().optional(),
  entry_place_id: Id.nullable().optional(),
  /**
   * Replaces the whole night list, like `place_ids`.
   *
   * Add-only would make unassigning a night impossible without a second endpoint, and unassigning is a
   * thing users do — they pick a hotel, then decide to leave that night open.
   */
  nights: z.array(TripNight).optional(),
  /** Replaces the whole arrangement, like `place_ids` and `nights`. */
  place_days: z.array(TripPlaceDay).optional(),
  /** Replaces the whole list of rest days. */
  rest_days: z.array(z.int().gte(1)).optional(),
}).meta({ id: "UpdateTripRequest", description: "PATCH /trips/:id. `place_ids`, `nights` and `place_days` replace." });

export type UpdateTripRequest = z.infer<typeof UpdateTripRequest>;

/**
 * A trip with its constraints and its selection.
 *
 * **`place_ids` is a set, not an ordered list, and that is a schema limitation rather than a design
 * choice.** The selection lives in `trip_constraint_places`, which has no `position` and not even a
 * `created_at`, so the order a user picked their places in cannot be stored. Said here because the field
 * name reads like a promise: a client must not present these as an order. A one-column migration is the
 * fix, and until then the order returned is deterministic but arbitrary.
 */
export const TripResponse = z.object({
  trip: Trip,
  constraints: TripConstraints,
  /** The places currently selected for this trip, in the user's order. */
  place_ids: z.array(Id),
  /**
   * Every night of the trip, oldest first, including the ones with nothing assigned.
   *
   * The server enumerates them rather than the client deriving them, because the count depends on
   * `stay_last_night` and on the date range, and two places computing that arithmetic is two places that
   * can disagree about how many nights a trip has.
   */
  nights: z.array(TripNight),
  /**
   * The user's arrangement of places into days, oldest day first.
   *
   * Empty means they have not arranged anything and the planner computes the days — which is the honest
   * default and the state every trip was in before this existed.
   */
  place_days: z.array(TripPlaceDay),
  /** The days the party comes back to its lodging to rest. */
  rest_days: z.array(z.int().gte(1)),
}).meta({ id: "TripResponse", description: "A trip with its constraints, its selection and its nights." });

export type TripResponse = z.infer<typeof TripResponse>;

// ------------------------------------------------------------------------------------ the plan

export const PlanStrategy = z.enum(["relax", "balanced", "explore"]).meta({
  id: "PlanStrategy",
  description: "The three shapes a plan can take. Distinct from the user's stated pace.",
});

/**
 * One stop in a plan.
 *
 * `start_min` is minutes from the start of the day rather than a clock time, because the day's start
 * depends on `wake_time` and the schedule is derived, not chosen. A client renders it with the trip's
 * own offset — and `null` means no schedule could be derived, which is a different statement from
 * "midnight".
 */
export const PlanStop = z.object({
  place_id: Id.nullable().describe("Null for a stop the user typed by hand rather than picked from a place."),
  custom_title: z.string().nullable(),
  name: z.string().describe("Resolved for display: the place's name, or the custom title."),
  position: z.int().gte(0),
  start_min: z.int().gte(0).nullable(),
  /**
   * How long the stop takes, or null when nobody said.
   *
   * Nullable because the database permits it (`duration_min IS NULL OR duration_min > 0`) and because
   * the two cases are different facts: the planner assigns a per-category default to a generated stop,
   * while a stop the user typed by hand may carry no estimate at all. `0` is not an option — a stop that
   * takes no time is not a stop.
   */
  duration_min: z.int().positive().nullable(),
  /**
   * What this stop is.
   *
   * `entry` is the day's own beginning on day 1 — the bus station, the airport — which is a place the route
   * starts from rather than one the party visits. It is distinct from `travel`, which is free for a derived
   * travel stop: one enum value per meaning, so a switch over these stays exhaustive as the planner grows.
   */
  kind: z.enum(["visit", "travel", "rest", "meal", "lodging", "entry"]),
  note: z.string().nullable(),
  /**
   * Where this stop is, or null when it cannot be placed on a map.
   *
   * Three cases produce null and all three are ordinary: a hand-typed stop has no place at all, a place
   * the user saved without geocoding is a complete asset that simply cannot be routed to (AC-2.5), and a
   * place deleted since the plan was made no longer has a location to read.
   *
   * Never estimated. The roadmap draws a pin only where it has one, and says how many stops it could not
   * place — a pin at a guessed coordinate would put a stop on the wrong road, which is the failure the
   * whole `location: null` convention exists to prevent.
   */
  location: z.object({ lat: z.number(), lon: z.number() }).nullable(),
  /**
   * True when the place this item referenced has been deleted (AC-4.10).
   *
   * The item survives with its name so the plan is never silently corrupted, and the flag is how the
   * client knows to offer a replacement rather than rendering a stop that cannot be routed to.
   */
  place_deleted: z.boolean(),
}).meta({ id: "PlanStop", description: "A stop or a derived item such as travel or rest." });


/**
 * One leg between consecutive stops: what the provider measured for it, and the road it runs on.
 *
 * Both numbers are nullable together, and `null` means the provider could not answer. The client then
 * draws markers with no line and "chưa xác định" where the distance would go (spec S12's degradation
 * row); a straight line or a guessed distance here would be the one thing §18 forbids most explicitly.
 *
 * **A leg carries its own `geometry`, and that reverses an earlier decision.** The previous version of
 * this comment refused the field: OSRM's trip service returned *one* path for a whole day, so a per-leg
 * path could only be a copy of the day's — and a client drawing leg by leg would stroke the same line
 * four times for a five-stop day. The premise was wrong in one detail. `/route` and `/trip` do return
 * per-leg roads: not as a field on the leg, but as its `steps[].geometry`, which joined in order *is*
 * the leg. Asking for them (`steps=true`) took a real two-leg Đà Lạt response from 856 B to 17,682 B
 * (measured 2026-09-29) — about 20×, and the provider meters calls rather than bytes, so an arrangement
 * edit costs no quota. With that paid, the copy problem disappears: each leg's path is its own piece of
 * road, and the day's line is their union rather than a fourth stroke over the same string.
 *
 * A leg whose steps are empty — or whose steps do not join end to end — is `null`, not a straight line
 * between the leg's ends. A straight line is a road nobody drove, and this codebase refuses invented
 * measurements everywhere else; the map already has a state for a leg with no path.
 */
export const RouteLeg = z.object({
  from_place_id: Id.nullable(),
  to_place_id: Id.nullable(),
  distance_m: z.int().gte(0).nullable(),
  duration_s: z.int().gte(0).nullable(),
  /**
   * This leg's own road as an encoded polyline (precision 5), or null when the provider gave no path
   * for it. Never a straight line between the leg's ends.
   */
  geometry: z.string().nullable(),
}).meta({ id: "RouteLeg", description: "A leg's measured distance and duration, and its own road path." });

export const PlanDay = z.object({
  day_index: z.int().gte(1),
  date: IsoDate,
  summary: z.string().nullable(),
  stops: z.array(PlanStop),
  legs: z.array(RouteLeg).describe("One fewer than `stops` when every leg was resolved."),
  /** Null when routing was unavailable. Never `0`. */
  distance_m: z.int().gte(0).nullable(),
  driving_min: z.int().gte(0).nullable(),
  /**
   * Uninterrupted rest this day, minutes, or null when it is not stored per day.
   *
   * The plan's total rest lives on `PlanVariant.total_rest_s`, which is where the planner writes it.
   * There is no per-day column and therefore no per-day figure to report, so this is `null` rather than
   * `0`: zero minutes of rest is a specific claim about the itinerary, and the app has no basis for it.
   */
  rest_min: z.int().gte(0).nullable(),
  /**
   * The day's whole road path, for drawing one line — the union of `legs[].geometry`.
   *
   * The roadmap draws a day as a single line, so it reads this; a client that wants the sections of the
   * day reads each leg's own `geometry` instead. The two cannot disagree, because this is the legs joined
   * end to end: the day has one road and the legs are its pieces, so there is no second, separately
   * simplified copy to drift.
   *
   * Null when the provider did not answer for this day — or when none of its legs got a path — in which
   * case the client draws the stop markers and says the distances are unknown. It must not connect them
   * with straight lines, which would misrepresent the journey by a measured factor of 2.24×.
   */
  geometry: z.string().nullable(),
  geometry_format: z.enum(["polyline5", "geojson"]).nullable(),
  /** Constraints this day could not satisfy, named. */
  unsatisfied: z.array(z.string()),
}).meta({ id: "PlanDay", description: "One day: its stops, its legs, and what it could not satisfy." });

export type PlanDay = z.infer<typeof PlanDay>;

/**
 * A plan variant with its real metrics.
 *
 * These four numbers are what the option cards show (AC-4.2), and the database comment is explicit
 * that they are planner-computed and never model output. `null` propagates from the legs rather than
 * being summed as zero: a total of `0` would read as "this plan involves no driving", when the truth
 * is that nobody measured it.
 */
export const PlanVariant = z.object({
  id: Id,
  strategy: PlanStrategy,
  label: z.string(),
  total_distance_m: z.int().gte(0).nullable(),
  total_driving_s: z.int().gte(0).nullable(),
  total_rest_s: z.int().gte(0).nullable(),
  place_count: z.int().gte(0),
  /** Constraints the plan could not satisfy, named explicitly (AC-4.5). */
  unsatisfied: z.array(z.string()),
  is_selected: z.boolean(),
  created_at: IsoDateTime,
}).meta({ id: "PlanVariant", description: "A plan's shape and its computed metrics. Never model output." });

export type PlanVariant = z.infer<typeof PlanVariant>;
export type GeneratedPlanVariant = z.infer<typeof GeneratedPlanVariant>;

/**
 * Whether the routing provider answered for this plan.
 *
 * Same three states as the place lookup, for the same reason: `unavailable` means the user should
 * retry, `quota_exceeded` means the app has hit a free-tier ceiling and will recover, and `ok` with
 * zero days means something else entirely. Collapsing them into a boolean would make the degradation
 * banner unable to say which.
 */
export const CreatePlansRequest = z.object({
  /**
   * Which strategies to generate. Omitted means all three, which is what the screen shows.
   *
   * Present so a client can regenerate one variant after changing a constraint without paying for the
   * other two — each plan costs routing calls against a service limited to a few requests per second.
   */
  strategies: z.array(PlanStrategy).min(1).optional(),
}).meta({ id: "CreatePlansRequest", description: "POST /trips/:id/plans. Omitted strategies means all three." });

/**
 * A variant as just generated, which knows one thing a stored one cannot.
 *
 * `pool_limited` is a property of the *generation run*: it says the candidate pool was no larger than
 * the strategy's capacity, so every strategy picked the same places and the three options are the same
 * route. It is not stored, because it stops being true the moment the user saves another place — a
 * stored flag would go stale, and a stale flag here would tell the user their plans differ when they
 * no longer do.
 *
 * `core` computes it (`Plan.poolLimited`) and its own comment says why the caller needs it: presenting
 * three identical plans as a choice implies a trade-off that is not there. The user's fix is to save
 * more places, which is a useful thing to learn, and the screen can only say so if the flag reaches it.
 */
export const GeneratedPlanVariant = PlanVariant.extend({
  pool_limited: z.boolean().describe("True when the pool was too small for the strategies to differ."),
}).meta({ id: "GeneratedPlanVariant", description: "A freshly generated variant, with its pool-limited flag." });

export const CreatePlansResponse = z.object({
  variants: z.array(GeneratedPlanVariant),
  /** The state of the provider, so an empty `variants` is never mistaken for "no such plan". */
  routing_status: ProviderStatus,
}).meta({ id: "CreatePlansResponse", description: "The generated variants and whether routing was available." });

/**
 * The variants a trip already has, for reopening it.
 *
 * Distinct from `CreatePlansResponse` in two ways that are both facts rather than omissions.
 *
 * There is no `pool_limited`: it describes a generation run, and a stored plan is not one (see
 * `GeneratedPlanVariant`). The question it answered — "were the three options the same route?" — is
 * unanswerable after the fact and no longer load-bearing, because the user has already made the choice.
 *
 * There is no `routing_status` for the same reason there is no `pool_limited`: nothing is being routed
 * now. `PlanVariant.total_distance_m` is `null` when the provider did not answer at generation time,
 * which is where that fact lives once the plan is stored.
 *
 * Empty is a real state and it is not failure: a trip is created before it has plans (`draft`), and
 * the client has to tell "no plans yet" from "these plans have no numbers".
 */
export const TripPlansResponse = z.object({
  variants: z.array(PlanVariant),
}).meta({ id: "TripPlansResponse", description: "A trip's stored variants, newest-first per strategy." });

export type TripPlansResponse = z.infer<typeof TripPlansResponse>;

export type CreatePlansResponse = z.infer<typeof CreatePlansResponse>;

export const PlanVariantDetail = z.object({
  variant: PlanVariant,
  trip: Trip,
  days: z.array(PlanDay),
}).meta({ id: "PlanVariantDetail", description: "One plan in full: its variant, its trip context, and its days." });

export type PlanVariantDetail = z.infer<typeof PlanVariantDetail>;

export const SelectPlanRequest = z.object({
  selected: z.boolean().default(true).describe("False clears the selection, leaving the trip in `draft`."),
}).meta({ id: "SelectPlanRequest", description: "POST /trips/:id/plans/:variantId/select." });

// ---------------------------------------------------------------------------- why this plan

/**
 * One reason, with its evidence.
 *
 * **A reason without evidence is not shown.** The UX plan is explicit: if the evidence is missing the
 * reason does not appear, because a planner that explains itself with a sentence nobody can check is
 * less trustworthy than one that stays quiet. `evidence` is therefore required.
 */
export const PlanExplanation = z.object({
  reason: z.string().describe("Vietnamese, one sentence, naming a fact about this plan."),
  /**
   * The numbers behind the reason.
   *
   * `null` values are allowed inside because a reason may rest on something unmeasured — and when it
   * does, the client can see that rather than take the sentence on faith.
   */
  evidence: z.record(z.string(), z.number().nullable()),
}).meta({ id: "PlanExplanation", description: "A reason and the numbers behind it." });

export const PlanExplanationsResponse = z.object({
  /**
   * Who wrote the prose.
   *
   * `planner` means the facts came with no wording, which is the degraded state from AC-6.1: the model
   * was unavailable, so the client renders the evidence list and the product stays fully usable. The
   * distinction is in the contract because the client's rendering differs.
   */
  generated_by: z.enum(["planner", "llm"]),
  explanations: z.array(PlanExplanation),
}).meta({ id: "PlanExplanationsResponse", description: "Why this plan looks the way it does." });

// ---------------------------------------------------------------------------- the roadmap

/**
 * A day's road geometry, for the roadmap map.
 *
 * `distances_known` exists because the map and the numbers degrade together: when routing was
 * unavailable the client draws markers, states "chưa xác định" where each leg's distance would be, and
 * shows a banner. A single flag is enough to drive all three, and without it each client would
 * re-derive the same conclusion from a null.
 */
export const DayRouteResponse = z.object({
  day_index: z.int().gte(1),
  date: IsoDate,
  geometry: z.string().nullable(),
  geometry_format: z.enum(["polyline5", "geojson"]).nullable(),
  legs: z.array(RouteLeg),
  distance_m: z.int().gte(0).nullable(),
  driving_min: z.int().gte(0).nullable(),
  distances_known: z.boolean(),
}).meta({ id: "DayRouteResponse", description: "One day's road path and legs, for drawing." });

export const TripRoutesResponse = z.object({
  days: z.array(DayRouteResponse),
  routing_status: ProviderStatus,
}).meta({ id: "TripRoutesResponse", description: "Every day's route for a variant." });

export type TripRoutesResponse = z.infer<typeof TripRoutesResponse>;

/** `PUT /trips/:id/plans/:variantId/days/:dayIndex/order` — the day's complete new order. */
export const PutDayOrderRequest = z.object({
  place_ids: z.array(Id).describe("Exactly the stops currently in this day, in the intended order."),
}).meta({ id: "PutDayOrderRequest", description: "The complete new order for one day. A partial list is rejected." });

// ------------------------------------------------------------------------------ the weather

/**
 * Forecast for a trip's window.
 *
 * `status` mirrors the weather provider's own three states, and `out_of_range` is the one that matters:
 * measured, Open-Meteo answers HTTP 400 past its horizon, so a far-future trip is a normal case that
 * must not be reported as an outage. `days` is empty when out of range and the client hides the row
 * rather than estimating (AC-6.2).
 */
export const TripWeatherResponse = z.object({
  status: z.enum(["ok", "out_of_range", "unavailable"]),
  days: z.array(z.object({
    date: IsoDate,
    temp_max_c: z.number().nullable(),
    temp_min_c: z.number().nullable(),
    precipitation_mm: z.number().nullable(),
    /** Null when the provider did not say, never `0` — zero rain and unknown rain are different facts. */
    precipitation_probability: z.number().nullable(),
    wind_max_kmh: z.number().nullable(),
    /**
     * The provider's own WMO weather code.
     *
     * A number rather than a translated string, because the code is a fact and the sentence is a
     * presentation of it. The client owns the mapping to Vietnamese, which means the copy can change
     * with a client release instead of requiring the API to redeploy — and an unknown code renders as
     * "chưa xác định" rather than as an empty cell.
     */
    weather_code: z.int().nullable(),
  })),
}).meta({ id: "TripWeatherResponse", description: "A trip's forecast, or why there is none." });

export type PlanStop = z.infer<typeof PlanStop>;
export type RouteLeg = z.infer<typeof RouteLeg>;
export type PlanExplanation = z.infer<typeof PlanExplanation>;
export type PlanExplanationsResponse = z.infer<typeof PlanExplanationsResponse>;
export type TripWeatherResponse = z.infer<typeof TripWeatherResponse>;
export type DayRouteResponse = z.infer<typeof DayRouteResponse>;
export type CreatePlansRequest = z.infer<typeof CreatePlansRequest>;
export type SelectPlanRequest = z.infer<typeof SelectPlanRequest>;
export type TripConstraintsInput = z.input<typeof TripConstraints>;
